import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PatOverviewBlockConfig } from '@front-platform/content-builder';
import { patOverviewBlockResolveConfig } from '@front-platform/content-builder';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Component, OnInit, input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { DateUtils } from '@shared/utils/date/date.utils';
import { PriceUtils } from '@shared/utils/price/price.utils';
import { CurrencyUtils } from '@shared/utils/currency/currency.utils';

import { Paths } from '@config/paths.config';
import { CheckKeys } from '@config/keys/check.keys';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';

import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { PatOverviewBlockPreviewBootstrapService } from './pat-overview-block-preview-bootstrap.service';

import {
  CheckTransactionSources,
  DoodCheckTransactionStatus,
  DoodOpenCheckItem,
} from '@core/models/check.model';
import { PaymentAtTableStoreRefiner } from '@common/refiners/payment-at-table.refiner';
import { PaymentAtTableStoreSelector } from '@common/selectors/payment-at-table.selector';
import { PaymentAtTableCheckError } from '@app/stores/payment-at-table/payment-at-table.state';
import { ShopsApiService } from '@core/services/api/shops/shops-api.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { TranslateService } from '@ngx-translate/core';
import { PatAvailabilityModalComponent } from '@shared/modals/pat-availability-modal/pat-availability-modal.component';
import { OnSiteLocationStoreSelector } from '@common/selectors/on-site-location.selector';
import { OnSiteLocationsService } from '@core/services/on-site-locations/on-site-locations.service';
import { PaymentAtTableStoreDispatcher } from '@common/dispatchers/payment-at-table.dispatcher';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';
import { AnalyticsService } from '@core/services/app/analytics.service';
import {
  isPatMockMode,
  PAT_ERROR_CHECK_LOCKED,
  PAT_ERROR_NO_CHECKS_OPEN,
  PAT_ERROR_NOT_FOUND,
  PAT_MOCK_QUERY_PARAMS,
  resolvePatMockCheckId,
} from '@config/values/pat.values';
export interface PatOverviewBlockRuntimeConfig {}

export type PatOverviewBlockConfigInput = Partial<
  Omit<PatOverviewBlockConfig, keyof PatOverviewBlockRuntimeConfig> & PatOverviewBlockRuntimeConfig
>;
export type PatOverviewBlockResolvedConfig = Omit<
  PatOverviewBlockConfig,
  keyof PatOverviewBlockRuntimeConfig
> &
  Partial<PatOverviewBlockRuntimeConfig>;
export interface PatOverviewBlockContract extends ConfigComponentContract<
  PatOverviewBlockConfigInput,
  PatOverviewBlockResolvedConfig
> {}

@Component({
  selector: 'app-pat-overview',
  templateUrl: './pat-overview-block.component.html',
  standalone: false,
  providers: [PatOverviewBlockPreviewBootstrapService],
})
export class PatOverviewBlockComponent
  extends DestroyerBase
  implements OnInit, PatOverviewBlockContract
{
  readonly config = input<PatOverviewBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    PatOverviewBlockConfig,
    PatOverviewBlockRuntimeConfig
  >(patOverviewBlockResolveConfig, () => ({ config: this.config() }));
  shop$ = this.paymentAtTableSelector.selectCheckShop;
  items$ = this.paymentAtTableSelector.selectCheckItems;
  onsiteLocation$ = this.paymentAtTableSelector.selectCheckOnSiteLocation;
  checkId$ = this.paymentAtTableSelector.selectCheck.pipe(map(check => check.id));

  finalPrice$ = combineLatest([
    this.paymentAtTableSelector.selectCheckFinalPrice,
    this.marketplaceSelector.selectMarketplaceCurrency,
  ]).pipe(map(([price, currency]) => PriceUtils.formatAndRoundUpPrice(price, currency ?? 'EUR')));

  error: PaymentAtTableCheckError | null = null;
  errorMessage: string | null = null;
  checkTransactionSources = CheckTransactionSources;
  checkTransactionStatuses = DoodCheckTransactionStatus;
  isLoading = false;

  error$ = this.paymentAtTableSelector.selectCheckError;
  errorMessage$ = this.paymentAtTableRefiner.selectErrorMessage;
  transactions$ = this.paymentAtTableSelector.selectCheckTransactions;

  outstandingAmount = '';
  currencySymbol!: string;
  goToChooseAmountInProgress = false;
  checkItemsById = new Map<string, { name: string; price: number }>();

  // Ajout de l'état du bouton reload
  reloadState: 'idle' | 'loading' | 'success' = 'idle';

  /** Item ids whose check line description is shown expanded (full text). */
  private readonly expandedCheckItemDescriptionIds = new Set<string>();

  private lastPatCheckId: string | undefined;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly routerHelper: RouterHelperService,
    private readonly patOverviewPreviewBootstrap: PatOverviewBlockPreviewBootstrapService,
    private marketplaceSelector: MarketplaceStoreSelector,
    private paymentAtTableRefiner: PaymentAtTableStoreRefiner,
    private paymentAtTableSelector: PaymentAtTableStoreSelector,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly shopApiService: ShopsApiService,
    private readonly modalsService: ModalsService,
    private readonly translateService: TranslateService,
    private readonly onSiteLocationSelector: OnSiteLocationStoreSelector,
    private readonly onSiteLocationsService: OnSiteLocationsService,
    private readonly paymentAtTableDispatcher: PaymentAtTableStoreDispatcher,
    private readonly paymentDispatcher: PaymentStoreDispatcher,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();

    this.paymentAtTableSelector.selectCheckError
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(_error => {
        this.error = _error;
      });
    this.paymentAtTableRefiner.selectErrorMessage
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(_message => {
        this.errorMessage = _message;
      });
    combineLatest([
      this.paymentAtTableSelector.selectCheckOutstandingAmount,
      this.marketplaceSelector.selectMarketplaceCurrency,
    ])
      .pipe(
        takeUntil(this._destroyerRef),
        map(([price, currency]) => PriceUtils.formatAndRoundUpPrice(price, currency ?? 'EUR')),
      )
      .subscribe(_price => {
        this.outstandingAmount = _price;
      });

    this.checkId$.pipe(takeUntil(this._destroyerRef), distinctUntilChanged()).subscribe(id => {
      if (id !== this.lastPatCheckId) {
        this.lastPatCheckId = id;
        this.expandedCheckItemDescriptionIds.clear();
      }
    });

    this.paymentAtTableSelector.selectCheck
      .pipe(
        takeUntil(this._destroyerRef),
        filter(check => !!check?.shop?.id && !!check?.marketplace?.id),
        take(1),
        switchMap(check => this.shopApiService.getShopById$(check.shop.id, check.marketplace.id)),
        tap(shop => {
          const patPlugin = shop.plugins.find(plugin => {
            return !plugin.plugin_enabled && plugin.type === 'PAY_AT_TABLE';
          });
          if (patPlugin === undefined) {
            return;
          }
          this.modalsService.open(PatAvailabilityModalComponent.handle);
          this.modalsService.setData(PatAvailabilityModalComponent.handle, {
            message: this.translateService.instant('pay-at-table.service-not-available'),
          });
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      `${Paths.PayAtTable}/${Paths.PayAtTableOverview}`,
      this.activatedRoute.snapshot,
    );
    this.currencySymbol = CurrencyUtils.getCurrencySymbol(
      this.marketplaceSelector.marketplace.currency,
    );

    // Build items map for quick lookup
    this.items$.pipe(takeUntil(this._destroyerRef)).subscribe(items => {
      this.checkItemsById.clear();
      items?.forEach(item => {
        this.checkItemsById.set(item.id, { name: item.name, price: item.price });
      });
    });

    this.reloadOpenCheck();
  }

  goToChooseAmount(): void {
    this.isLoading = true;
    this.router.navigate(
      [this.routerHelper.translateRoute(`${Paths.PayAtTable}/${Paths.PayAtTableChooseAmount}`)],
      {
        queryParams: isPatMockMode(resolvePatMockCheckId(this.activatedRoute.snapshot))
          ? PAT_MOCK_QUERY_PARAMS
          : undefined,
      },
    );
  }

  forceType(data: any): any {
    return data as any;
  }

  shouldShowCheckItemDescription(item: DoodOpenCheckItem): boolean {
    const desc = item.description?.trim();
    return Boolean(desc && desc !== item.name?.trim());
  }

  isCheckItemDescriptionExpanded(itemId: string): boolean {
    return this.expandedCheckItemDescriptionIds.has(itemId);
  }

  onCheckItemRowClick(item: DoodOpenCheckItem): void {
    if (!this.shouldShowCheckItemDescription(item)) {
      return;
    }
    if (this.expandedCheckItemDescriptionIds.has(item.id)) {
      this.expandedCheckItemDescriptionIds.delete(item.id);
    } else {
      this.expandedCheckItemDescriptionIds.add(item.id);
    }
  }

  getTransactionItems(
    transactionItems: string[] | null | undefined,
  ): { name: string; price: number }[] {
    if (!transactionItems?.length || this.checkItemsById.size === 0) {
      return [];
    }

    return transactionItems
      .map(itemId => this.checkItemsById.get(itemId))
      .filter((item): item is { name: string; price: number } => Boolean(item));
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return '';
    return `${DateUtils.formatLocalizedDate(dateString, this.translateService.instant('date.short-format'))} ${DateUtils.dayjsInMarketplaceTimezone(dateString).format(this.translateService.instant('order-details.format'))}`;
  }

  formatTime(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString(this.translateService.currentLang, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  onReloadClick(): void {
    this.reloadOpenCheck();
  }

  private reloadOpenCheck(): void {
    if (this.reloadState === 'loading') {
      return;
    }

    if (isPatMockMode(resolvePatMockCheckId(this.activatedRoute.snapshot))) {
      return;
    }

    const { id, lastLocationId } = this.onSiteLocationSelector.settings;
    const onSiteLocationId = id ?? lastLocationId;

    this.analyticsService.trackEvent('get_check_on_location', {
      on_site_location_id: onSiteLocationId,
    });

    const check = this.paymentAtTableSelector.check;
    if (
      check?.onsite_location?.id &&
      onSiteLocationId &&
      check.onsite_location.id !== onSiteLocationId
    ) {
      this.paymentAtTableDispatcher.reset();
    }

    if (!onSiteLocationId) {
      this.paymentAtTableDispatcher.updateCheck({
        error: {
          detail: PAT_ERROR_NOT_FOUND,
        },
      });
      return;
    }

    this.reloadState = 'loading';

    this.onSiteLocationsService
      .loadOnSiteLocationOpenCheck$(onSiteLocationId)
      .pipe(take(1), takeUntil(this._destroyerRef))
      .subscribe({
        next: result => {
          if (!result) {
            this.analyticsService.trackEvent('get_check_on_location_no_check', {
              on_site_location_id: onSiteLocationId,
            });
            this.paymentAtTableDispatcher.updateCheck({
              error: {
                detail: PAT_ERROR_NO_CHECKS_OPEN,
              },
            });
            this.reloadState = 'idle';
            return;
          }

          this.paymentAtTableDispatcher.updateCheck({ ...result, error: null });
          this.analyticsService.trackEvent('get_check_on_location_success', {
            on_site_location_id: result.onsite_location?.id,
            on_site_location_name: result.onsite_location?.name,
            check_id: result.id,
            check_opened_at: result.opened_at,
            check_shop: result.shop,
            check_final_price: result.final_price,
            check_currency: result.currency,
          });

          const paygreenStatus = this.activatedRoute.snapshot.queryParams['paygreen_status'];
          const transaction = this.activatedRoute.snapshot.queryParams['transactionId'];
          if ((paygreenStatus === 'authorized' || paygreenStatus === 'successed') && transaction) {
            this.router.navigate([
              this.routerHelper.translateRoute(
                `/${Paths.PayAtTable}/${Paths.PaymentConfirmation}/${transaction}`,
              ),
            ]);
          }

          if (result?.[CheckKeys.AvailablePaymentMethods]?.length) {
            this.paymentDispatcher.updateMethods(
              result.available_payment_methods
                .filter(method => method.valid)
                .map(method => method.payment_method),
            );
          }

          this.reloadState = 'success';
          setTimeout(() => {
            this.reloadState = 'idle';
          }, 3000);
        },
        error: err => {
          const errorDetail =
            err?.error?.error === PAT_ERROR_CHECK_LOCKED
              ? PAT_ERROR_CHECK_LOCKED
              : err?.error?.detail;
          this.analyticsService.trackEvent('get_check_on_location_error', {
            error_message: errorDetail,
          });
          this.paymentAtTableDispatcher.updateCheck({
            error: errorDetail ? { detail: errorDetail } : null,
          });
          this.reloadState = 'idle';
        },
      });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PatOverviewBlockComponent, { 'keyAliases': {} });
